// Preload cache — stores main section HTML keyed by product handle.
var _pfsCache = {};
// Live DOM cache — stores the rendered <section id="shopify-section-main">
// node keyed by handle. On swap-back we re-attach this exact node instead
// of rebuilding from cached HTML, so the customer keeps the already-painted
// video posters (.video-review-item has `background: #000`, so any 50-200ms
// poster decode/paint gap on remount reads as a hard black blink — even
// with HTTP-cached posters). Populated lazily on swap-AWAY in switchProduct.
var _pfsDomCache = {};
// URLs we've already pushed into the browser HTTP cache via off-DOM Image().
var _pfsPosterWarmed = new Set();

// Defer a callback until the browser's next idle window (200ms timeout
// fallback if the main thread stays busy). Used to push the non-visible
// post-swap re-init work — Shopify PaymentButton.init(), Judge.me
// customWidgets(), Loox init(), and the section:load script re-activation
// loop — off the user-perceived swap path so the pill animation and the
// newly-mounted product section paint first. Falls back to setTimeout(0)
// for browsers without rIC (older Safari).
function _pfsDeferIdle(fn) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(fn, { timeout: 200 });
  } else {
    setTimeout(fn, 0);
  }
}

// Loading state for a bundle card. Cards swap the product like a tab does, but
// the pill stays on "Bundle & Save" for them, so nothing on screen answered
// the tap until the new product arrived: about 0.5 s on a fast desktop and
// 1-4.5 s on a throttled phone (lab, 2026-09-21). The card now takes the
// selected look in the tap's own frame, with a spinner if the wait passes
// 200 ms, and the grid is marked busy. The swap replaces the card with fresh
// server markup; a failed switch clears the state here, and so does a cached
// reattach (_pfsClearCardLoading), since the stashed node still carries it.
function _pfsSetCardLoading(card, on) {
  card.classList.toggle('pfs__bundle-card--loading', on);
  var grid = card.closest && card.closest('.pfs__bundle-grid');
  if (!grid) return;
  if (on) grid.setAttribute('aria-busy', 'true');
  else grid.removeAttribute('aria-busy');
}

function _pfsClearCardLoading(root) {
  root.querySelectorAll('.pfs__bundle-card--loading').forEach(function (card) {
    card.classList.remove('pfs__bundle-card--loading');
  });
  root.querySelectorAll('.pfs__bundle-grid[aria-busy]').forEach(function (grid) {
    grid.removeAttribute('aria-busy');
  });
}

// ---------------------------------------------------------------------------
// Keyboard focus across a switch
//
// A switch replaces the whole main section, and the activated button goes with
// it. The destination renders the product now showing as a non-focusable
// <div> (pfs__option--active, pfs__bundle-card--active), so focus fell back to
// <body>: a screen reader lost its place and Tab restarted from the top of the
// page. After a switch started from the keyboard, focus now moves to the
// destination's entry for the new product. Pointer users see no change.
// homepage-product-switcher.js uses the same three helpers for its own swap,
// through window.JiYuSwitcherFocus below.
// ---------------------------------------------------------------------------

// True for a click from the keyboard or assistive tech. Enter and Space on a
// <button>, and a screen reader's activate command, fire a click with detail 0;
// a mouse click or a tap counts from 1. :focus-visible covers a keyboard user
// whose click carries a count anyway. isTrusted keeps a script's el.click(),
// also detail 0, from moving anyone's focus.
function _pfsKeyboardActivated(e, el) {
  if (!e || !e.isTrusted) return false;
  if (e.detail === 0) return true;
  try {
    return el.matches(':focus-visible');
  } catch (err) {
    return false;
  }
}

// Focus the entry for handle inside root, of the same kind as the activated
// control (a card for a card, a strip tab for a tab, since the bundles tab and
// the current card carry the same handle). First the entry for the product now
// showing, which the server draws as a <div>: a strip can also list that
// product as a button (a bundle in its own family list, seen on the homepage
// embed), and that button is not the active option. Then any entry of that
// kind, then any entry with the handle. A <div> gets tabindex="-1". It keeps
// it: the next switch replaces the node anyway, and removing it on blur would
// drop focus to <body> when the customer leaves the window and comes back.
//
// preventScroll, then a reveal only when needed: on a phone the entry is
// usually where the customer was looking, so nothing moves. On desktop the
// switch has just scrolled to the top, which left a bundle card below the fold
// at 1440x900, so a sighted keyboard user could not see where focus went. An
// entry outside the middle 80% of the viewport is centred, not merely brought
// inside it: content above the switcher can still grow after the swap (113 px
// seen on the toner PDP), which pushed an entry sitting at the bottom edge out
// of view. focusVisible: this only ever runs for a keyboard user, so
// ask for the ring outright rather than rely on each engine's heuristic for a
// focus moved by script after the focused element was removed. Engines
// without the option ignore it.
function _pfsFocusEntry(root, handle, isCard) {
  if (!root || !handle) return false;
  var attr = '[data-handle="' + (window.CSS && CSS.escape ? CSS.escape(handle) : handle) + '"]';
  var kind = isCard ? '.pfs__bundle-card' : '.pfs__option';
  var candidates = [
    root.querySelector(kind + ':not(button)' + attr),
    root.querySelector(kind + attr),
    root.querySelector(attr)
  ];
  for (var i = 0; i < candidates.length; i++) {
    var el = candidates[i];
    // No box (a grid hidden in lead view, a card outside the ?fb scope): it
    // cannot take focus.
    if (!el || !el.getClientRects().length) continue;
    if (el.tabIndex < 0 && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true, focusVisible: true });
    if (document.activeElement !== el) continue;
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight;
    if (r.top < vh * 0.1 || r.bottom > vh * 0.9 || r.left < 0 || r.right > window.innerWidth) {
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
    }
    return true;
  }
  return false;
}

// Whether focus should follow the swap of region: focus is inside region (the
// swap is about to take it), already lost to <body>, or still where it was at
// the click. The last covers a screen reader that activates a button from its
// virtual cursor without focusing it first. Checked right before the swap, so
// a customer who moved on while the destination downloaded (Tab to the
// header, the cart drawer) keeps their place.
function _pfsFocusWithin(region, focusAtClick) {
  var active = document.activeElement;
  if (!active || active === document.body || active === focusAtClick) return true;
  return !!(region && region.contains(active));
}

window.JiYuSwitcherFocus = {
  keyboardActivated: _pfsKeyboardActivated,
  focusEntry: _pfsFocusEntry,
  focusWithin: _pfsFocusWithin
};

// ---------------------------------------------------------------------------
// Intent prefetch (bundle destinations only)
//
// A switch fetches the destination's full page (about 190 KB over the wire,
// 1 MB of HTML), and that download is nearly the whole wait. The fetch used to
// start only after every click listener on the page had run: on a 4x-throttled
// phone that was ~120 ms after the click, most of it the Meta Pixel's click
// listener, which runs first. For a bundle card or the Bundle & Save tab it now
// starts at the first sign of intent: pointerup for touch and pen (a scroll
// ends in pointercancel instead, so scrolling past the cards never downloads
// anything), pointerdown or an 80 ms hover for a mouse. switchProduct reuses
// the response when it builds the same URL. Measured on a throttled phone, the
// new bundle appeared ~120 ms sooner; on desktop the hover head start is larger.
//
// Family tabs are left out on purpose: alternate-template tests target them
// (see "Alternate-template (?view=) support" below), and a visitor in a
// variant arm would download the default page for nothing, competing with the
// variant's own fetch. If a test ever targets a bundle, this path still falls
// back to a fresh fetch, but exclude that bundle here too. The view resolver is
// only ever called on the click, so a prefetch never enrolls anyone.
// ---------------------------------------------------------------------------
var PFS_PREFETCH_TTL_MS = 30000;
var PFS_HOVER_INTENT_MS = 80;
var _pfsPrefetches = {};

function _pfsPrefetch(url) {
  var now = Date.now();
  Object.keys(_pfsPrefetches).forEach(function (key) {
    if (now - _pfsPrefetches[key].at >= PFS_PREFETCH_TTL_MS) delete _pfsPrefetches[key];
  });
  if (_pfsPrefetches[url]) return;
  var entry = {
    at: now,
    html: fetch(url).then(function (r) {
      if (!r.ok) throw new Error('Prefetch failed: ' + r.status);
      return r.text();
    })
  };
  // Drop a failed prefetch so the click fetches again; the catch also keeps an
  // unconsumed failure from surfacing as an unhandled rejection.
  entry.html.catch(function () {
    if (_pfsPrefetches[url] === entry) delete _pfsPrefetches[url];
  });
  _pfsPrefetches[url] = entry;
}

// The prefetched HTML promise for url, or null. Single use.
function _pfsTakePrefetch(url) {
  var entry = _pfsPrefetches[url];
  delete _pfsPrefetches[url];
  if (!entry || Date.now() - entry.at >= PFS_PREFETCH_TTL_MS) return null;
  return entry.html;
}

// ---------------------------------------------------------------------------
// Alternate-template (?view=) support
//
// Shopify renders templates/product.<suffix>.json when a PDP URL carries
// ?view=<suffix>. A feature or A/B test can opt a specific product into an
// alternate template by defining:
//
//   window.JiYuProductViewResolver = function (handle) {
//     // return a suffix string, '' for "use the default template", or a
//     // Promise of either. Returning a non-Promise keeps the path synchronous.
//   };
//
// switchProduct consults it before building its fetch URL, so the destination
// PDP renders through the alternate template with no redirect. The suffix is
// applied to the FETCH url only, never to pushState — the address bar keeps the
// canonical product URL (no leaked test URLs, no canonical/SEO surprises).
//
// Contract:
//   - Called at most once per handle per page (memoized), and ONLY for the
//     handle actually being switched to. A resolver gated on an A/B assignment
//     can therefore treat the call itself as the exposure event without
//     enrolling visitors who never open that product.
//   - Capped by PFS_VIEW_TIMEOUT_MS so a slow or hung resolver can never stall a
//     switch. On timeout this switch proceeds with the default template; a late
//     answer is still cached so the next switch gets it for free.
// ---------------------------------------------------------------------------
var PFS_VIEW_TIMEOUT_MS = 350;
var _pfsViewCache = {};
var _pfsViewPending = {};

function _pfsResolveView(handle) {
  if (Object.prototype.hasOwnProperty.call(_pfsViewCache, handle)) {
    return Promise.resolve(_pfsViewCache[handle]);
  }
  var resolver = window.JiYuProductViewResolver;
  if (typeof resolver !== 'function') return Promise.resolve('');
  if (_pfsViewPending[handle]) return _pfsViewPending[handle];

  var promise = new Promise(function (resolve) {
    var settled = false;
    function finish(suffix) {
      if (settled) return;
      settled = true;
      resolve(suffix);
    }

    setTimeout(function () { finish(''); }, PFS_VIEW_TIMEOUT_MS);

    try {
      Promise.resolve(resolver(handle)).then(
        function (value) {
          var suffix = typeof value === 'string' ? value : '';
          _pfsViewCache[handle] = suffix;
          delete _pfsViewPending[handle];
          finish(suffix);
        },
        function () {
          delete _pfsViewPending[handle];
          finish('');
        }
      );
    } catch (e) {
      delete _pfsViewPending[handle];
      finish('');
    }
  });

  _pfsViewPending[handle] = promise;
  return promise;
}

// ---------------------------------------------------------------------------
// Intelligems copies (TASKS #133)
//
// The price test redirects a shopper in a test group to Intelligems' copy of
// each product in the test (see "Copies" in intelligems-bridge.js). Intelligems
// only redirects full page loads, so a switch fetches the copy itself: its
// variants, prices and Stay.ai plans are what checkout charges. Options keep
// the original handles in data-handle; the fetch, the prefetch and the address
// bar use the copy. homepage-product-switcher.js does the same.
//
// The answer is unknown until Intelligems is ready. A click then waits for it,
// capped at PFS_IG_WAIT_MS, behind the loading state it already shows; a
// prefetch skips instead of downloading a page the shopper may never see.
// Without the bridge (Intelligems uninstalled) every handle stands for itself.
// ---------------------------------------------------------------------------
var PFS_IG_WAIT_MS = 2000;

// { handle, variantId, known } for the option's product, as of now.
function _pfsCopyNow(handle, variantId) {
  var ig = window.JiyuIntelligems;
  if (!ig || typeof ig.productFor !== 'function') {
    return { handle: handle, variantId: variantId || null, known: true };
  }
  return ig.productFor(handle, variantId);
}

// The same, once Intelligems is ready or the wait runs out.
function _pfsResolveCopy(handle, variantId) {
  var ig = window.JiyuIntelligems;
  if (!ig || typeof ig.whenReady !== 'function') return Promise.resolve(_pfsCopyNow(handle, variantId));
  return ig.whenReady(PFS_IG_WAIT_MS).then(function () {
    return _pfsCopyNow(handle, variantId);
  });
}

// ---------------------------------------------------------------------------
// External section scripts inside injected HTML
//
// Swapped-in section HTML is parsed with DOMParser and adopted, which marks
// every <script> in it unexecutable — and the inline-script re-creation below
// deliberately skips src scripts. So a section whose behavior lives in an
// external asset (transformation-timeline.js, comparison-table-v2.js, ...)
// arrives as DEAD MARKUP whenever the destination template has a section the
// host page didn't. This loads exactly those missing assets.
//
// Only assets NOT already on the page are loaded, so a running script is never
// re-executed. _pfsKnownScripts is seeded from the server-rendered document
// (whose scripts really did run) before any injection can pollute it — reading
// the DOM after an injection would see the inert copies and wrongly conclude
// they were already loaded.
// ---------------------------------------------------------------------------
var _pfsKnownScripts = new Set();

function _pfsScriptFile(src) {
  if (!src) return '';
  return src.split('?')[0].split('/').pop();
}

function _pfsSeedKnownScripts() {
  document.querySelectorAll('script[src]').forEach(function (script) {
    var file = _pfsScriptFile(script.getAttribute('src'));
    if (file) _pfsKnownScripts.add(file);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _pfsSeedKnownScripts, { once: true });
} else {
  _pfsSeedKnownScripts();
}

function _pfsLoadMissingSectionScripts(container) {
  if (!container) return;
  container.querySelectorAll('script[src]').forEach(function (inert) {
    var src = inert.getAttribute('src');
    var file = _pfsScriptFile(src);
    if (!file || _pfsKnownScripts.has(file)) return;
    _pfsKnownScripts.add(file);
    var script = document.createElement('script');
    script.src = src;
    script.defer = true;
    document.head.appendChild(script);
  });
}

// Resolve the live main product section node. Section IDs on OS 2.0 JSON
// templates are template-prefixed (shopify-section-template--<id>__main), so
// the legacy hardcoded getElementById('shopify-section-main') is ALWAYS null
// on this theme. The family selector lives INSIDE the main section, so the
// nearest "#MainContent > .shopify-section" ancestor of it is the main
// section. Fall back to <product-info>, then the legacy id, for safety.
function getMainProductSection() {
  var sel = document.querySelector('[data-product-family-selector]');
  var sec = sel && sel.closest && sel.closest('#MainContent > .shopify-section');
  if (sec) return sec;
  var info = document.querySelector('#MainContent > .shopify-section product-info');
  if (info && info.closest) return info.closest('#MainContent > .shopify-section');
  return document.getElementById('shopify-section-main');
}

// Expanded-mode preservation across swaps.
//
// Each PDP template configures the family-switcher block with its own
// compact_mode setting: toner + moisturizer PDPs render compact (2
// options: self + bundle), bundle PDP renders the full 3 options. A
// customer who lands on the bundle PDP sees all 3; if they then click
// moisturizer, the destination template renders compact and they'd
// drop to 2 options mid-browse. That's the bug — they implicitly
// chose the expanded view by visiting bundle, and we should keep
// showing it.
//
// _pfsExpandedSnapshot captures the current option set any time a
// switcher renders or augments to 3+ visible options. On a subsequent
// compact render, _pfsAugmentToExpanded re-inserts the missing
// siblings before init() wires click handlers, so the customer keeps
// seeing the full family.
//
// Respects the existing `?from=<handle>` flow (compact-context sticky
// nav): if the customer is in that mode, we leave them alone — they
// explicitly chose a 2-option browse context. Snapshot is in-memory
// and session-scoped; a full page reload starts fresh.
//
// _pfsDefaultVariants caches `data-default-variant` per handle as we
// see it on rendered buttons. The snapshot's active entry is a `<div>`
// with no data-default-variant attribute, so when we convert it back
// to a `<button>` for re-insertion we look up the variant id here.
var _pfsExpandedSnapshot = null;
var _pfsDefaultVariants = {};

function _pfsCaptureExpandedSnapshot(container) {
  if (!container) return;
  var options = container.querySelector('.pfs__options');
  if (!options) return;

  var visibleOptions = Array.from(options.querySelectorAll('.pfs__option'))
    .filter(function (el) { return el.style.display !== 'none'; });
  if (visibleOptions.length < 3) return;

  var snapshot = { handles: [], elements: {} };
  visibleOptions.forEach(function (el) {
    var handle = el.getAttribute('data-handle');
    if (!handle) return;
    snapshot.handles.push(handle);
    snapshot.elements[handle] = el.cloneNode(true);
    var dv = el.getAttribute('data-default-variant');
    if (dv) _pfsDefaultVariants[handle] = dv;
  });
  _pfsExpandedSnapshot = snapshot;
}

function _pfsAugmentToExpanded(container) {
  if (!_pfsExpandedSnapshot || !container) return;

  // Respect the existing compact-context sticky-nav flow.
  var urlParams;
  try { urlParams = new URLSearchParams(window.location.search); } catch (e) { return; }
  if (urlParams.get('from')) return;

  var options = container.querySelector('.pfs__options');
  if (!options) return;

  var newActive = options.querySelector('.pfs__option--active');
  var newActiveHandle = newActive ? newActive.getAttribute('data-handle') : '';

  // Family-mismatch guard: if we've navigated to a product that wasn't in
  // the snapshot, the snapshot is from a different family and stale.
  // Drop it and let the page render as-is.
  if (newActiveHandle && _pfsExpandedSnapshot.handles.indexOf(newActiveHandle) === -1) {
    _pfsExpandedSnapshot = null;
    return;
  }

  var visibleOptions = Array.from(options.querySelectorAll('.pfs__option'))
    .filter(function (el) { return el.style.display !== 'none'; });
  if (visibleOptions.length >= _pfsExpandedSnapshot.handles.length) return;

  var currentHandles = new Set();
  options.querySelectorAll('.pfs__option').forEach(function (el) {
    currentHandles.add(el.getAttribute('data-handle'));
  });

  _pfsExpandedSnapshot.handles.forEach(function (handle, snapshotIdx) {
    if (currentHandles.has(handle)) return;

    var template = _pfsExpandedSnapshot.elements[handle];
    if (!template) return;
    var clone = template.cloneNode(true);

    // The snapshot's active entry is a `<div class="pfs__option--active">`;
    // when re-inserted on a page where it's no longer active, convert it
    // back to a `<button>` so it's clickable. data-default-variant comes
    // from the harvested cache (the snapshot's div didn't have it).
    if (clone.classList.contains('pfs__option--active') && handle !== newActiveHandle) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pfs__option';
      btn.setAttribute('data-handle', handle);
      btn.setAttribute('data-product-family-switch', '');
      var dv = _pfsDefaultVariants[handle];
      if (dv) btn.setAttribute('data-default-variant', dv);
      btn.innerHTML = clone.innerHTML;
      clone = btn;
    }

    // Insert in canonical (snapshot) order: find the first existing option
    // whose snapshot index is greater than this one and slot ours before it.
    var existingOptions = options.querySelectorAll('.pfs__option');
    var inserted = false;
    for (var i = 0; i < existingOptions.length; i++) {
      var nextHandle = existingOptions[i].getAttribute('data-handle');
      var nextIdx = _pfsExpandedSnapshot.handles.indexOf(nextHandle);
      if (nextIdx > snapshotIdx) {
        options.insertBefore(clone, existingOptions[i]);
        inserted = true;
        break;
      }
    }
    if (!inserted) options.appendChild(clone);
  });
}

// Walk a piece of cached product HTML for `<video data-poster>` and the
// `<img src>` overlays and fallbacks used by video_reviews blocks, then
// attach an `<img>` for each into a hidden offscreen container. Bare `new
// Image()` warms the HTTP cache but doesn't reliably pre-decode the
// bitmap — that's what makes the actual paint instant on a section
// swap. Anchoring the warmed `<img>` in the document forces the
// browser to fully decode the bitmap once and keep it live in the
// image cache, so the eager poster `<img>` in main-product.liquid's
// video_reviews block reuses it on first mount instead of paying
// another decode/paint cycle.
function _pfsGetPosterWarmContainer() {
  var c = document.getElementById('pfs-poster-warm-container');
  if (!c) {
    c = document.createElement('div');
    c.id = 'pfs-poster-warm-container';
    c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;contain:strict;';
    document.body.appendChild(c);
  }
  return c;
}

function warmVideoPosterCache(html) {
  if (!html) return;
  var doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch (e) {
    return;
  }
  var urls = new Set();
  doc.querySelectorAll('.video-review-item video[data-poster]').forEach(function (v) {
    var u = v.getAttribute('data-poster');
    if (u) urls.add(u);
  });
  doc.querySelectorAll('.video-review-item img[src]').forEach(function (img) {
    var u = img.getAttribute('src');
    if (u) urls.add(u);
  });
  if (urls.size === 0) return;
  var container = _pfsGetPosterWarmContainer();
  urls.forEach(function (u) {
    if (_pfsPosterWarmed.has(u)) return;
    _pfsPosterWarmed.add(u);
    var img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.src = u;
    container.appendChild(img);
  });
}

// The video_reviews poster overlays render loading="lazy" (sections/main-
// product.liquid, TASKS #143) so a page load never fetches the hidden
// layout's copies or the ones far below the fold. A swapped-in section is
// different: the customer may be looking at the block as it mounts, and iOS
// Safari holds a lazy image in swapped-in DOM until after first paint, a
// black blink on the #000 item. Before insertion, make the copies this
// breakpoint shows eager + high again, as the markup had them.
// homepage-product-switcher.js does the same for its injected section.
function _pfsEagerVideoPosters(root) {
  var layout = window.matchMedia('(min-width: 750px)').matches ? 'desktop' : 'mobile';
  root.querySelectorAll('.reviews-block-video-' + layout + ' img.video-review-eager-poster').forEach(function (img) {
    img.setAttribute('loading', 'eager');
    img.setAttribute('fetchpriority', 'high');
  });
}

class ProductFamilySelector {
  constructor(element) {
    // Skip if this exact element is already wired up. Prevents stacking
    // click handlers on the same options, and — important for the slide
    // animation — avoids initSlider's --no-transition step interrupting
    // the in-progress pill slide that switchProduct just kicked off on
    // this same selector.
    if (element._pfsInstance) return;
    this.container = element;
    element._pfsInstance = this;

    // Compute homepage-switcher context first: it gates both the PDP-style
    // click wiring (homepage embeds have their own swap model) AND the
    // expanded-mode preservation (snapshot/augment) below.
    //
    // When this selector is rendered inside the homepage product switcher
    // (homepage-product-switcher.js), that section's own click handler swaps
    // content into its own wrapper instead of replacing #shopify-section-main.
    // Skip PDP-style click wiring, but still position the sliding green pill
    // and scroll the active option into view — those are pure visual setup
    // and the homepage selector needs them to look right.
    var insideHomepageSwitcher = element.closest && element.closest('[data-homepage-product-switcher]');

    // The snapshot/augment "sticky family" feature re-projects a previously
    // seen expanded family onto a later compact page. That fights the v3
    // bundles model, where every template defines its own strip server-side
    // (a 2-tab lead page like Toner, a 4-tab bundle page). Re-projecting would
    // both re-expand a 2-tab page's strip AND duplicate the page-specific
    // "Bundle & Save" tab — its data-handle differs per page (current bundle
    // vs first bundle), so the augment's dedupe-by-handle misses it. Skip
    // snapshot + augment for v3 blocks so each page's server render is
    // authoritative. (Legacy compact_mode pages, which have neither class,
    // keep the sticky-family behavior.)
    var isV3Bundles = element.classList &&
      (element.classList.contains('product-family-block--with-bundles') ||
        element.classList.contains('product-family-block--bundle-grid'));
    var skipExpandedSnapshot = insideHomepageSwitcher || isV3Bundles;

    // If the customer has seen a 3-option switcher earlier in this session
    // (e.g. landed on the bundle PDP), re-insert the missing siblings BEFORE
    // we capture allOptions, so init() wires click handlers on the augmented
    // buttons too. See _pfsAugmentToExpanded for the rules.
    if (!skipExpandedSnapshot) {
      _pfsAugmentToExpanded(element);
    }

    this.allOptions = element.querySelectorAll('[data-handle]');
    this.currentHandle = this.getCurrentHandle();
    this.isLoading = false;
    if (!insideHomepageSwitcher) {
      // Apply ?from=<handle> filter BEFORE initSlider so the pill is
      // measured against the post-filter layout (hidden options change
      // the active option's offsetLeft via flex: 1 1 0% redistribution).
      this.applyFromFilter();
      this.applyBundleScopeFilter();
      this.init();
      this.initBundleTabs();
      this.syncProductInfoView();
    }
    this.initSlider();
    this.scrollActiveIntoView();
    // Multi-stage retry to cover progressively-late layout shifts in the
    // homepage-product-switcher inject path:
    //
    //   - Immediate: catches direct-PDP visits where layout is already settled.
    //   - 2x RAF: catches the typical case where the constructor runs in the
    //     same microtask as the shopify:section:load dispatch (scrollWidth /
    //     clientWidth haven't been remeasured against the new DOM yet).
    //   - 300ms timeout: catches async work kicked off by re-executed inline
    //     scripts and lazy image loads that reflow the container after RAFs.
    //   - window.load: catches font swap + remaining lazy image loads if the
    //     initial page hasn't finished loading by the time PFS constructs.
    //
    // Each call is idempotent — once scrollLeft lands at the correct target,
    // later calls re-set the same value (no visible flicker). The retries
    // also no-op the early-bail check (scrollWidth <= clientWidth) once
    // overflow exists, so non-overflowing strips (e.g. compact mode) cost
    // a few cheap getter reads and nothing else.
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { self.scrollActiveIntoView(); });
    });
    setTimeout(function () { self.scrollActiveIntoView(); }, 300);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { self.scrollActiveIntoView(); }, { once: true });
    }

    // Snapshot this rendered + augmented + ?from-filtered state for the
    // next swap. _pfsCaptureExpandedSnapshot itself bails when fewer than
    // 3 options are visible, so compact PDPs that didn't get augmented
    // are no-ops. Skipped for v3 blocks (see skipExpandedSnapshot above).
    if (!skipExpandedSnapshot) {
      _pfsCaptureExpandedSnapshot(element);
    }
  }

  // Preserve compact-mode browse context across navigation. When the
  // switcher renders in compact mode (current product + 1 sibling) and
  // the customer clicks the sibling, append ?from=<currentHandle> to
  // the destination URL. On the destination — even if its own template
  // renders the full 3-option family — applyFromFilter() hides any
  // option that isn't the active product or the `from` product, so the
  // customer keeps seeing a 2-option switcher for the rest of their
  // browse session.
  //
  // Returns the handle to forward as `from`, or null if no context.
  // Priority:
  //   1. Existing ?from=<x> in URL — keep the family root sticky as
  //      the customer hops between siblings.
  //   2. Current product handle, IF the switcher is in compact mode
  //      (exactly one non-active option visible). This is the case
  //      where the current product IS the family root.
  //   3. null (3-option full family — no context to preserve).
  getOriginHandle() {
    var urlParams = new URLSearchParams(window.location.search);
    var existingFrom = urlParams.get('from');
    if (existingFrom) return existingFrom;

    var visibleNonActive = Array.from(
      this.container.querySelectorAll('.pfs__option:not(.pfs__option--active)')
    ).filter(function (el) { return el.style.display !== 'none'; });
    if (visibleNonActive.length === 1) {
      return this.currentHandle;
    }
    return null;
  }

  // Hide options that aren't the active product or the ?from=<handle>
  // sibling. Bails if `from` doesn't match an actual option in the
  // switcher, so a stale or malformed param can't blank the UI.
  applyFromFilter() {
    var urlParams = new URLSearchParams(window.location.search);
    var fromHandle = urlParams.get('from');
    if (!fromHandle) return;

    var keepActive = this.container.querySelector('.pfs__option--active');
    if (!keepActive) return;

    var fromOption = this.container.querySelector(
      '[data-handle="' + (window.CSS && CSS.escape ? CSS.escape(fromHandle) : fromHandle) + '"]'
    );
    if (!fromOption || fromOption === keepActive) return;

    this.container.querySelectorAll('.pfs__option').forEach(function (opt) {
      if (opt === keepActive) return;
      if (opt === fromOption) return;
      opt.style.display = 'none';
    });

    // When the filter runs to completion, exactly two options remain visible
    // (the active tab + the `from` sibling). Flag the block so CSS can fill
    // them 50/50 — the structural two-tab rule in the stylesheet keys off
    // :nth-child(2):last-child, which counts the display:none'd siblings and
    // therefore never matches in the filtered case.
    this.container.classList.add('product-family-block--from-filtered');
  }

  // Origin-aware bundle scoping. When a visitor clicks "Bundle & Save" on a
  // LEAD PDP, that lead's curated bundle grid (its bundles_tab_products) is the
  // set of bundles relevant to that lead. Carry those handles forward as
  // ?fb=<h1,h2,...> so every bundle PDP reached in this browse only shows those
  // bundles — hiding bundles that don't belong to the lead the visitor came
  // from (e.g. the Glow-Up bundle, which has no exfoliant, for someone who
  // started on the Exfoliant). Direct bundle visits have no ?fb and show every
  // card, so the curated narrowing is scoped to the lead -> bundle flow only.
  //
  // Returns the comma-joined allowed-bundle handles, or null. Mirrors
  // getOriginHandle():
  //   1. Existing ?fb=<...> in the URL — sticky across bundle-to-bundle hops.
  //   2. The current LEAD's rendered grid card handles, but ONLY when the click
  //      is navigating INTO a bundle (the Bundles tab or a bundle card). A
  //      bundle PDP (--bundles-active) never establishes a fresh scope from its
  //      own full grid; a lead -> lead family-tab hop never establishes one.
  getBundleScope(option) {
    var existing = new URLSearchParams(window.location.search).get('fb');
    if (existing) return existing;
    if (!option) return null;
    var intoBundle = (option.getAttribute && option.getAttribute('data-pfs-bundles-tab') !== null) ||
                     (option.classList && option.classList.contains('pfs__bundle-card'));
    if (!intoBundle) return null;
    if (this.container.classList.contains('product-family-block--bundles-active')) return null;
    var cards = Array.prototype.slice.call(
      this.container.querySelectorAll('.pfs__bundle-card[data-handle]')
    );
    if (cards.length === 0) return null;
    return cards.map(function (c) { return c.getAttribute('data-handle'); }).join(',');
  }

  // Hide bundle grid cards not in the carried ?fb=<...> scope. No-op without
  // ?fb (direct visits show every card). The active card is always kept, and
  // if nothing would remain the filter bails (never blanks the grid) — same
  // defensive posture as applyFromFilter. Retiles the grid to the visible-card
  // count via data-visible-count so the row stays filled instead of leaving an
  // empty column (CSS handles the column count).
  applyBundleScopeFilter() {
    var fb = new URLSearchParams(window.location.search).get('fb');
    if (!fb) return;
    var allowed = fb.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (allowed.length === 0) return;
    var grid = this.container.querySelector('.pfs__bundle-grid');
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.pfs__bundle-card[data-handle]'));
    if (cards.length === 0) return;
    var keepers = cards.filter(function (card) {
      return card.classList.contains('pfs__bundle-card--active') ||
             allowed.indexOf(card.getAttribute('data-handle')) !== -1;
    });
    if (keepers.length === 0 || keepers.length === cards.length) return;
    cards.forEach(function (card) {
      card.style.display = keepers.indexOf(card) !== -1 ? '' : 'none';
    });
    grid.setAttribute('data-visible-count', String(keepers.length));
  }

  init() {
    this.allOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = option.dataset.handle;
        if (handle && handle !== this.currentHandle && !this.isLoading) {
          this.switchProduct(handle, option, _pfsKeyboardActivated(e, option));
        }
      });
      var bundleDestination = option.classList.contains('pfs__bundle-card') || option.hasAttribute('data-pfs-bundles-tab');
      if (bundleDestination && option.hasAttribute('data-product-family-switch')) this.wireIntentPrefetch(option);
    });
  }

  // Start the destination's download on intent rather than on click. See
  // "Intent prefetch (bundle destinations only)" at the top of this file.
  wireIntentPrefetch(option) {
    var self = this;
    var hoverTimer = null;
    function prefetch() {
      clearTimeout(hoverTimer);
      var handle = option.dataset.handle;
      if (!handle || handle === self.currentHandle || self.isLoading) return;
      var copy = _pfsCopyNow(handle, option.dataset.defaultVariant);
      if (!copy.known) return;
      _pfsPrefetch(self.productPath(option, false, null, copy));
    }
    option.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(prefetch, PFS_HOVER_INTENT_MS);
    });
    option.addEventListener('pointerleave', function () { clearTimeout(hoverTimer); });
    option.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') prefetch();
    });
    option.addEventListener('pointerup', function (e) {
      if (e.pointerType !== 'mouse') prefetch();
    });
  }

  // Bundle-grid mode tabs. The lead tab on a bundle PDP carries data-handle
  // and is already wired by init() above — it triggers switchProduct() like
  // any other family-selector option. The tabs that need NEW JS are the
  // pure view-toggle ones (no data-handle, or data-handle === currentHandle)
  // on the lead PDP: clicking them swaps `data-pfs-view` without any fetch.
  initBundleTabs() {
    var self = this;
    var tabs = this.container.querySelectorAll('[data-pfs-tab]');
    if (tabs.length === 0) return;

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        var handle = tab.dataset.handle;
        // If this tab points to a DIFFERENT product, init()'s click handler
        // on the same element will trigger switchProduct(). Skip the view
        // toggle — the destination page renders with its own initial view.
        if (handle && handle !== self.currentHandle) return;

        e.preventDefault();
        var targetView = tab.dataset.pfsTab; // 'lead' or 'bundles'
        self.setBundleGridView(targetView);
      });
    });
  }

  // Toggle between 'lead' and 'bundles' view on the LEAD PDP. Moves the
  // .pfs__option--active class between the two tabs, slides the green
  // pill to match, and writes data-pfs-view to both the family-block
  // element AND the <product-info> ancestor so CSS in the latter can
  // hide variant picker + ATC + price when bundles view is active.
  setBundleGridView(view) {
    if (view !== 'lead' && view !== 'bundles') return;
    var current = this.container.getAttribute('data-pfs-view');
    if (current === view) return;

    this.container.setAttribute('data-pfs-view', view);

    var leadTab = this.container.querySelector('[data-pfs-tab="lead"]');
    var bundleTab = this.container.querySelector('[data-pfs-tab="bundles"]');
    if (leadTab && bundleTab) {
      if (view === 'bundles') {
        leadTab.classList.remove('pfs__option--active');
        leadTab.setAttribute('aria-selected', 'false');
        bundleTab.classList.add('pfs__option--active');
        bundleTab.setAttribute('aria-selected', 'true');
      } else {
        bundleTab.classList.remove('pfs__option--active');
        bundleTab.setAttribute('aria-selected', 'false');
        leadTab.classList.add('pfs__option--active');
        leadTab.setAttribute('aria-selected', 'true');
      }
      var newActive = this.container.querySelector('.pfs__option--active');
      if (newActive) this.updateSliderPosition(newActive);
    }

    this.syncProductInfoView();
  }

  // Push the current data-pfs-view value onto the <product-info> ancestor
  // so its descendant CSS rules (hide variant picker, hide ATC, hide price)
  // can key off it. Called from the constructor (initial paint) AND from
  // setBundleGridView (on toggle).
  //
  // On constructor run, also resets data-pfs-view to data-pfs-initial-view
  // if both are present. This handles the cached-DOM re-attach case: the
  // user's prior tab-toggle state (e.g. switched to bundles on lead PDP)
  // would otherwise persist when they navigate back via "Exfoliant" tab
  // and we re-attach the cached DOM. data-pfs-initial-view is set by
  // Liquid and never mutated by JS, so it's always the destination's
  // intended starting view.
  syncProductInfoView() {
    var initial = this.container.getAttribute('data-pfs-initial-view');
    if (initial) {
      this.container.setAttribute('data-pfs-view', initial);
    }
    var view = this.container.getAttribute('data-pfs-view');
    if (!view) return;
    var productInfo = this.container.closest('product-info');
    if (productInfo) productInfo.setAttribute('data-pfs-view', view);
  }

  // The product this page stands for, as the options name it. On an
  // Intelligems copy the URL holds the copy's handle, so prefer the one Liquid
  // renders on the block (snippets/ig-original-handle.liquid).
  getCurrentHandle() {
    var selfHandle = this.container && this.container.getAttribute('data-pfs-self-handle');
    if (selfHandle) return selfHandle;
    return window.location.pathname.split('/products/')[1]?.replace(/\?.*/, '').replace(/\/$/, '') || '';
  }

  initSlider() {
    var active = this.container.querySelector('.pfs__option--active');
    if (!active) return;
    var options = this.container.querySelector('.pfs__options');
    if (!options) return;
    options.classList.add('pfs__options--no-transition');

    var self = this;
    var insideHomepageSwitcher = this.container.closest
      && this.container.closest('[data-homepage-product-switcher]');

    // Inline + one rAF covers the common page-load and PDP-swap cases.
    // The rAF handles the swap case where offsets are transient because
    // the new section was just appended in the same synchronous block.
    self.updateSliderPosition(active);
    requestAnimationFrame(function () {
      self.updateSliderPosition(active);
      // Re-enable transitions so user clicks within the same instance slide
      // smoothly. For homepage-switcher embeds we KEEP no-transition on:
      // each tab click there destroys this whole section and mounts a fresh
      // selector, so there's never a pill to animate; any later position
      // correction (from the ResizeObserver below catching CSS load /
      // layout shift) needs to land instantly, not animate from the
      // pre-correction position into view (e.g. sliding up from the page
      // bottom because offsetTop was huge before flex layout applied).
      if (!insideHomepageSwitcher) {
        requestAnimationFrame(function () {
          options.classList.remove('pfs__options--no-transition');
        });
      }
    });

    // Safety net for the case where the inline + rAF measurements run
    // before component-product-family-selector.css finishes loading.
    // That CSS sets `flex: 1 1 0%` on each option so the three options
    // share width equally. Without it, each option claims the full row
    // width — measuring the active option then returns the container's
    // ~610px width, locking the pill at 0/610. When the CSS lands the
    // option's width drops to ~197px and ResizeObserver fires, so we
    // re-measure. We DO NOT disconnect after the first fire because
    // that first fire often carries the still-wrong width (any value
    // > 0 used to satisfy our check); subsequent fires from CSS load,
    // image load, or window resize then refresh the position. The
    // observer is GC'd along with the family selector element when
    // the section is replaced on a product switch.
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        if (active.offsetWidth > 0) {
          self.updateSliderPosition(active);
        }
      });
      ro.observe(active);
    }
  }

  updateSliderPosition(target) {
    var options = this.container.querySelector('.pfs__options');
    if (!options || !target) return;
    // Renamed --slide-left/--slide-top → --slide-x/--slide-y because they now
    // drive a transform translate (compositor-only) instead of left/top
    // (layout-triggering). Width/height vars are still read by CSS but change
    // instantly — see component-product-family-selector.css for the rationale.
    options.style.setProperty('--slide-x', target.offsetLeft + 'px');
    options.style.setProperty('--slide-y', target.offsetTop + 'px');
    options.style.setProperty('--slide-width', target.offsetWidth + 'px');
    options.style.setProperty('--slide-height', target.offsetHeight + 'px');
  }

  // On mobile the options row is a horizontal scroller; without this, a
  // direct visit to e.g. the bundle PDP would land with the bundle option
  // off-screen to the right. Center the active option in the viewport.
  // No-op on desktop / when there's no overflow.
  scrollActiveIntoView() {
    var options = this.container.querySelector('.pfs__options');
    var active = this.container.querySelector('.pfs__option--active');
    if (!options || !active) return;
    if (options.scrollWidth <= options.clientWidth) return;
    var targetLeft = active.offsetLeft - (options.clientWidth - active.offsetWidth) / 2;
    var maxLeft = options.scrollWidth - options.clientWidth;
    if (targetLeft < 0) targetLeft = 0;
    if (targetLeft > maxLeft) targetLeft = maxLeft;
    // Use 'auto' (instant) on initial load so the page doesn't appear to
    // animate sideways the moment the customer arrives.
    options.scrollTo({ left: targetLeft, top: 0, behavior: 'auto' });
  }

  // Build the destination URL for a sibling product. The button's
  // data-default-variant (rendered server-side as the 2nd variant id)
  // becomes ?variant=<id>, which makes Shopify pre-select that variant
  // in the rendered HTML — no post-swap radio click, no flash.
  // copy: _pfsCopyNow / _pfsResolveCopy's answer; when given, its handle and
  // variant (an Intelligems copy's, or the option's own) replace the option's.
  productPath(option, includeSectionId, viewSuffix, copy) {
    var original = option.dataset.handle;
    var handle = copy ? copy.handle : original;
    var variantId = copy ? copy.variantId : option.dataset.defaultVariant;
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var path = rootUrl + 'products/' + handle;
    var params = [];
    if (variantId) params.push('variant=' + encodeURIComponent(variantId));
    // ?view= and ?section_id= do NOT compose — Shopify ignores view and returns
    // the section from the product's DEFAULT template. Callers that need an
    // alternate template must therefore use the full-page path.
    if (includeSectionId) params.push('section_id=main-product');
    else if (viewSuffix) params.push('view=' + encodeURIComponent(viewSuffix));
    var fromHandle = this.getOriginHandle();
    if (fromHandle && fromHandle !== original) {
      params.push('from=' + encodeURIComponent(fromHandle));
    }
    var bundleScope = this.getBundleScope(option);
    if (bundleScope) {
      params.push('fb=' + encodeURIComponent(bundleScope));
    }
    if (params.length > 0) path += '?' + params.join('&');
    return path;
  }

  // Preload main product section for sibling products after page is idle
  preloadFamilyProducts() {
    // Skip preload when bundle_grid_mode is active (detected via data-pfs-view
    // attribute, which only the new mode emits). The existing preload uses
    // `section_id=main-product`, which Shopify renders with schema defaults
    // instead of template-saved settings — so the cached HTML would drop
    // bundle_grid_mode entirely. Slower swaps but correct rendering.
    // See CLAUDE.md Lessons Learned: "Shopify Section Rendering API renders
    // with SCHEMA DEFAULTS, not template-saved settings".
    if (this.container.hasAttribute('data-pfs-initial-view')) return;

    var self = this;
    this.allOptions.forEach(function(option) {
      var handle = option.dataset.handle;
      if (handle && handle !== self.currentHandle && !_pfsCache[handle]) {
        var copy = _pfsCopyNow(handle, option.dataset.defaultVariant);
        if (!copy.known) return;
        fetch(self.productPath(option, true, null, copy))
          .then(function(r) { return r.ok ? r.text() : null; })
          .then(function(html) {
            if (html && html.length > 100) {
              _pfsCache[handle] = html;
              warmVideoPosterCache(html);
            }
          })
          .catch(function() {});
      }
    });
  }

  // fromKeyboard: the switch was started from the keyboard or assistive tech
  // (_pfsKeyboardActivated), so focus follows it to the destination's entry.
  async switchProduct(newHandle, clickedButton, fromKeyboard) {
    if (this.isLoading) return;
    this.isLoading = true;
    var focusAtClick = document.activeElement;

    // A review video still downloading would share the connection with the
    // page fetch below, which then took 14.5 s instead of ~2 s (TASKS #177).
    if (window.JiYuVideoReviews && window.JiYuVideoReviews.stopAll) window.JiYuVideoReviews.stopAll();

    // Bundle cards (.pfs__bundle-card in the grid) also carry data-handle, so
    // they route through switchProduct too — but they are NOT tabs in the
    // strip. Switching between bundles must keep the active tab ("Bundle &
    // Save") and its pill stationary; only animate the pill when an actual
    // strip tab (.pfs__option) was clicked. Without this guard the pill slides
    // toward the clicked card's coordinates (offsetLeft/Top relative to the
    // grid, not the strip), which reads as the green background drifting.
    var isTab = clickedButton.classList && clickedButton.classList.contains('pfs__option');
    var isCard = !isTab && clickedButton.classList && clickedButton.classList.contains('pfs__bundle-card');
    if (isCard) _pfsSetCardLoading(clickedButton, true);

    // Immediately slide the green pill to the clicked tab (tabs only).
    var previousActive = this.container.querySelector('.pfs__option--active');
    if (isTab) {
      if (previousActive) previousActive.classList.remove('pfs__option--active');
      clickedButton.classList.add('pfs__option--active');
      clickedButton.classList.add('pfs__option--loading');
      this.updateSliderPosition(clickedButton);
    }

    var self = this;
    try {
      var cachedDom = _pfsDomCache[newHandle];

      // On mobile the family selector lives well below the fold, so
      // scrolling to the top yanks the customer away from where they
      // tapped. Skip the scroll on mobile and let them stay where they
      // were. Desktop still snaps to top so the new PDP starts from
      // the hero.
      var isMobile = window.matchMedia('(max-width: 749px)').matches;

      // Stash the currently-mounted main section into the live DOM cache
      // keyed by the handle we're leaving. Next time the customer comes
      // back to that product, we'll re-attach this exact node — video
      // posters already painted, variant + subscription state preserved —
      // instead of rebuilding from HTML and paying the decode/paint cost
      // that reads as a black flash on .video-review-item.
      //
      // Read the leaving handle straight from window.location, NOT from
      // this.currentHandle: when the previous switch took the cached-main
      // branch, the replacement PFS instance was constructed BEFORE the
      // URL was pushed, so its currentHandle stuck at the prior product.
      // The URL is the source of truth for what's currently mounted; it
      // hasn't been pushState'd to newHandle yet at this point. On an
      // Intelligems copy (TASKS #133) the URL holds the copy's handle, so the
      // mounted block's data-pfs-self-handle names it the way options do.
      var leavingHandle = '';
      var currentMainEl = getMainProductSection();
      var leavingBlock = currentMainEl && currentMainEl.querySelector('[data-product-family-selector][data-pfs-self-handle]');
      if (leavingBlock) leavingHandle = leavingBlock.getAttribute('data-pfs-self-handle');
      var urlMatch = window.location.pathname.match(/\/products\/([^/?]+)/);
      if (!leavingHandle && urlMatch) leavingHandle = urlMatch[1];
      if (leavingHandle && leavingHandle !== newHandle && currentMainEl) {
        _pfsDomCache[leavingHandle] = currentMainEl;
      }

      if (cachedDom) {
        // FAST PATH (fix 2): the live DOM cache holds the target product's
        // already-rendered main section (buy box, gallery, painted video
        // posters). Re-attach it in a macrotask — off the click's synchronous
        // processing time so the swap isn't attributed to the click's INP, but
        // via setTimeout(0), NOT requestAnimationFrame: rAF is PAUSED on hidden/
        // backgrounded tabs, so a customer who clicks then tabs away mid-switch
        // (or any headless/background context) would never get the reattach and
        // the swap-back would stick on the old product. Same reason open() and
        // openLoading use setTimeout. A macrotask still keeps the INP win.
        setTimeout(function () {
          if (!isMobile) window.scrollTo({ top: 0, behavior: 'instant' });
          var refocus = fromKeyboard && _pfsFocusWithin(currentMainEl, focusAtClick);
          if (currentMainEl && currentMainEl.parentNode) {
            currentMainEl.parentNode.replaceChild(cachedDom, currentMainEl);
          }
          // Re-sync the embedded family selector's active state. The cached
          // node was stashed mid-switch: switchProduct's opening pill animation
          // had already marked the PREVIOUS leave's destination active (and left
          // a --loading spinner on it), so the cached selector shows a STALE
          // active option. We no longer re-swap main from the fetch (fix 1), so
          // nothing else corrects it — reset it to the product we're switching
          // to NOW. (The current product's option is the div/button whose
          // data-handle === newHandle; see sections/product-family-selector.)
          var s = cachedDom.querySelector('[data-product-family-selector]');
          if (s) {
            s.querySelectorAll('.pfs__option').forEach(function (opt) {
              opt.classList.remove('pfs__option--active', 'pfs__option--loading');
            });
            var activeOpt = s.querySelector('[data-handle="' + newHandle + '"]');
            if (activeOpt) activeOpt.classList.add('pfs__option--active');

            // Keep the existing instance — re-constructing would double-wire the
            // option click handlers that persisted on the cached node (the
            // constructor guards against exactly this). Just refresh its handle
            // and pill position to match the reset active option.
            if (!s._pfsInstance) {
              new ProductFamilySelector(s);
            } else {
              s._pfsInstance.currentHandle = newHandle;
              if (activeOpt) s._pfsInstance.updateSliderPosition(activeOpt);
            }
          }
          // A card clicked on this node before it was stashed is still
          // showing its loading state.
          _pfsClearCardLoading(cachedDom);
          // After the reset above, so the entry is in its end state.
          if (refocus) _pfsFocusEntry(s, newHandle, isCard);

          // Non-visible re-init off the critical path.
          _pfsDeferIdle(function () {
            if (window.Shopify && window.Shopify.PaymentButton) {
              try { window.Shopify.PaymentButton.init(); } catch (e) {}
            }
          });
        }, 0);
      }

      // Resolve any alternate template (?view=<suffix>) for the destination.
      // Deliberately AFTER the cached-DOM reattach is scheduled above, so the
      // instant swap-back never waits on this. The pill animation and the
      // --loading spinner already painted synchronously, so the await is hidden
      // behind the loading state the customer is already looking at.
      var viewSuffix = await _pfsResolveView(newHandle);
      // The shopper's Intelligems copy of the destination, if any. Awaited
      // here for the same reason as the view.
      var copy = await _pfsResolveCopy(newHandle, clickedButton.dataset.defaultVariant);

      // Fetch full page for remaining sections (and as fallback if no cache).
      // Pass ?variant=<id> so the server pre-selects the 2nd variant option
      // for related products — keeps the merchandising default consistent
      // and avoids any post-swap radio-click flash.
      var fullPagePath = this.productPath(clickedButton, false, viewSuffix, copy);
      // The address bar always gets the canonical product URL — never ?view=.
      // For a copy that is the copy's URL, where Intelligems' own redirect
      // would have taken the shopper.
      var historyPath = viewSuffix ? this.productPath(clickedButton, false, null, copy) : fullPagePath;
      var prefetched = viewSuffix ? null : _pfsTakePrefetch(fullPagePath);
      var html = null;
      if (prefetched) {
        try { html = await prefetched; } catch (e) { html = null; }
      }
      if (html === null) {
        var response = await fetch(fullPagePath);
        if (!response.ok) throw new Error('Fetch failed: ' + response.status);
        html = await response.text();
      }
      // Update the URL now — same timing as before (right after the fetch
      // resolves). The title is set once the doc is parsed below.
      window.history.pushState({ productHandle: newHandle }, '', historyPath);

      if (cachedDom) {
        // FIX 3: the main/buy-box section is already painted from the live DOM
        // cache, so the ENTIRE fetch payload is below-fold (reviews, related
        // products, etc.). Parse it and swap the non-main sections in an idle
        // slot so the DOMParser + section replacement never block the main
        // thread during — or right after — the interaction.
        _pfsDeferIdle(function () {
          var newDoc = new DOMParser().parseFromString(html, 'text/html');
          document.title = newDoc.title || document.title;
          self.replaceNonMainSections(newDoc);

          // The selector lives inside the cached main (already has a PFS
          // instance from the rAF above), so this is normally a no-op — kept
          // as a guard in case the swap ever brings in a fresh, uninstanced
          // selector, to avoid a pill-render flicker.
          var refreshedSelector = document.querySelector('[data-product-family-selector]');
          if (refreshedSelector && !refreshedSelector._pfsInstance) {
            new ProductFamilySelector(refreshedSelector);
          }

          // Script reactivation + widget re-init is the least urgent work —
          // push it to a second idle slot.
          _pfsDeferIdle(function () { self.reinitNonMainSections(); });
          self.isLoading = false;
        });
      } else {
        // NO LIVE CACHE: the buy box only exists in the fetched HTML, so we
        // must parse to paint it. Parsing here is already OFF the click (it
        // runs in the fetch-resolution task, not the interaction). Paint the
        // new main first, then defer the heavier full re-init one task so the
        // swap doesn't run as a single long main-thread block.
        var newDoc = new DOMParser().parseFromString(html, 'text/html');
        document.title = newDoc.title || document.title;
        if (!isMobile) window.scrollTo({ top: 0, behavior: 'instant' });
        var refocus = fromKeyboard && _pfsFocusWithin(document.getElementById('MainContent'), focusAtClick);
        this.replaceSections(newDoc);
        this.activateInlineScripts();
        // The destination template can carry sections this page never had, whose
        // behavior lives in external assets that arrive inert. Load them.
        _pfsLoadMissingSectionScripts(document.getElementById('MainContent'));
        // Position the pill immediately so there's no flicker; reinitialize()
        // re-creating the instance is then a fast no-op (constructor guard).
        var sel = document.querySelector('[data-product-family-selector]');
        if (sel && !sel._pfsInstance) new ProductFamilySelector(sel);
        // After the constructor, whose ?from / ?fb filters decide what shows.
        if (refocus) _pfsFocusEntry(sel, newHandle, isCard);
        setTimeout(function () {
          self.reinitialize(newHandle);
          self.isLoading = false;
        }, 0);
      }
    } catch (error) {
      console.error('Product switch failed:', error);
      if (isCard) _pfsSetCardLoading(clickedButton, false);
      if (isTab) {
        clickedButton.classList.remove('pfs__option--active');
        clickedButton.classList.remove('pfs__option--loading');
        if (previousActive) {
          previousActive.classList.add('pfs__option--active');
          this.updateSliderPosition(previousActive);
        }
      }
      this.isLoading = false;
    }
  }

  // Activate inline scripts and dispatch shopify:section:load on the below-fold
  // (non-main) sections only, then re-init the review widgets. The main/buy-box
  // section is handled separately (live-DOM-cache reattach, or full
  // replaceSections on the no-cache path), so it's skipped here — re-running its
  // inline scripts would double-init the reattached node. Match the main
  // section by its __main key suffix, not the legacy hardcoded id.
  reinitNonMainSections() {
    _pfsReinitNonMainSections();
  }

  replaceNonMainSections(newDoc) {
    var currentMain = document.getElementById('MainContent');
    var newMain = newDoc.getElementById('MainContent');
    if (!currentMain || !newMain) return;

    // Map current sections by ID
    var currentSections = {};
    currentMain.querySelectorAll(':scope > .shopify-section').forEach(function(el) {
      currentSections[el.id] = el;
    });

    // Collect new section IDs before adopting (adoptNode removes from source)
    var newSectionList = Array.from(newMain.querySelectorAll(':scope > .shopify-section'));
    var newSectionIds = new Set(newSectionList.map(function(el) { return el.id; }));

    // Replace or add non-main sections
    newSectionList.forEach(function(newSection) {
      // Skip the main/buy-box section — it's swapped separately (live-DOM-cache
      // reattach or full replaceSections). Match by the __main template-key
      // suffix, since section IDs are template-prefixed (shopify-section-
      // template--<id>__main), NOT the literal 'shopify-section-main'.
      if (/__main$/.test(newSection.id)) return;
      var adopted = document.adoptNode(newSection);
      adopted.querySelectorAll('.scroll-trigger').forEach(function(el) {
        el.classList.add('scroll-trigger--cancel');
      });
      var existing = currentSections[adopted.id];
      if (existing) {
        existing.replaceWith(adopted);
      } else {
        currentMain.appendChild(adopted);
      }
    });

    // Remove sections that only exist in the old template
    Object.keys(currentSections).forEach(function(id) {
      if (/__main$/.test(id)) return;
      if (!newSectionIds.has(id) && currentSections[id].parentNode) {
        currentSections[id].remove();
      }
    });

    // Judge.me delivers reviews pre-rendered but hidden behind
    // display:none wrappers and a temp-hiding <style> tag.
    // Their entry point script normally removes these on page load,
    // but doesn't re-run after AJAX. Unhide them manually.
    this.reinitJudgeMe(currentMain);
  }

  reinitJudgeMe(container) {
    _pfsReinitJudgeMe(container);
  }

  replaceSections(newDoc) {
    var currentMain = document.getElementById('MainContent');
    var newMain = newDoc.getElementById('MainContent');
    if (!currentMain || !newMain) return;

    _pfsEagerVideoPosters(newMain);

    var newChildren = [];
    while (newMain.firstChild) {
      newChildren.push(document.adoptNode(newMain.firstChild));
    }

    currentMain.replaceChildren.apply(currentMain, newChildren);

    currentMain.querySelectorAll('.scroll-trigger').forEach(function(el) {
      el.classList.add('scroll-trigger--cancel');
    });

    this.reinitJudgeMe(currentMain);
  }

  activateInlineScripts() {
    this.activateInlineScriptsIn(document.getElementById('MainContent'));
  }

  activateInlineScriptsIn(container) {
    if (!container) return;
    container.querySelectorAll('script').forEach(function(oldScript) {
      if (oldScript.src) return;
      if (oldScript.type && oldScript.type !== 'text/javascript' && oldScript.type !== '') return;

      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function(attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  reinitialize(newHandle) {
    this.currentHandle = newHandle;

    var newSelector = document.querySelector('[data-product-family-selector]');
    if (newSelector) {
      new ProductFamilySelector(newSelector);
    }

    if (window.Shopify && window.Shopify.PaymentButton) {
      try { window.Shopify.PaymentButton.init(); } catch (e) {}
    }

    if (typeof jdgm !== 'undefined') {
      try {
        if (jdgm.customWidgets) jdgm.customWidgets();
      } catch (e) {}
    }

    if (window.looxReviews) {
      try { window.looxReviews.init(); } catch (e) {}
    }

    document.querySelectorAll('#MainContent .shopify-section').forEach(function(section) {
      try {
        section.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: section.id.replace('shopify-section-', '') }
        }));
      } catch (e) {}
    });
  }
}

// Judge.me delivers reviews pre-rendered but hidden behind display:none
// wrappers and a temp-hiding <style> tag. Their entry point script normally
// removes these on page load, but doesn't re-run after AJAX. Unhide manually.
function _pfsReinitJudgeMe(container) {
  if (!container) return;
  // Re-execute the inline data script that sets jdgm.data.reviewWidget[productId]
  container.querySelectorAll('.shopify-app-block script').forEach(function (oldScript) {
    if (oldScript.src) return;
    if (oldScript.textContent.indexOf('jdgm.data') === -1) return;
    var newScript = document.createElement('script');
    newScript.textContent = oldScript.textContent;
    oldScript.parentNode.replaceChild(newScript, oldScript);
  });

  // Reset Judge.me state so the widget can be re-processed
  if (typeof jdgm !== 'undefined') {
    jdgm._loaderExecuted = false;
  }
  container.querySelectorAll('.jdgm--done-setup-widget').forEach(function (el) {
    el.classList.remove('jdgm--done-setup-widget');
  });

  // Judge.me's review_widget.js is an ES module with a singleton that checks
  // `this.initialized`. Dynamic import with a cache-busting param creates a
  // fresh module instance that re-processes the widget.
  var entryScript = document.querySelector('script[src*="review_widget"]');
  if (entryScript) {
    var baseUrl = entryScript.src.split('?')[0];
    import(baseUrl + '?v=' + Date.now()).catch(function () {});
  }
}

// Activate inline scripts and dispatch shopify:section:load on the below-fold
// (non-main) sections only, then re-init the review widgets. The main/buy-box
// section is handled separately (live-DOM-cache reattach, or full
// replaceSections on the no-cache path), so it's skipped here — re-running its
// inline scripts would double-init the reattached node.
function _pfsReinitNonMainSections() {
  // Injected sections arrive with their <script src> marked unexecutable; pull
  // in any section asset this page doesn't already have.
  _pfsLoadMissingSectionScripts(document.getElementById('MainContent'));

  document.querySelectorAll('#MainContent .shopify-section').forEach(function (section) {
    if (/__main$/.test(section.id)) return;
    section.querySelectorAll('script').forEach(function (oldScript) {
      if (oldScript.src) return;
      if (oldScript.type && oldScript.type !== 'text/javascript' && oldScript.type !== '') return;
      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
    try {
      section.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: section.id.replace('shopify-section-', '') }
      }));
    } catch (e) {}
  });

  // Re-init review widgets for the below-fold sections.
  if (typeof jdgm !== 'undefined') {
    try { if (jdgm.customWidgets) jdgm.customWidgets(); } catch (e) {}
  }
  if (window.looxReviews) {
    try { window.looxReviews.init(); } catch (e) {}
  }
}

// Public hook for any feature that swaps the below-fold sections in place
// (e.g. an alternate-template A/B test on a direct PDP visit). Brings injected
// sections to life: missing section assets, inline scripts, section:load, and
// the review widgets. The main/buy-box section is left untouched.
window.JiYuHydrateSections = function (container) {
  _pfsReinitJudgeMe(container || document.getElementById('MainContent'));
  _pfsReinitNonMainSections();
};

// Initialize on page load
document.addEventListener('DOMContentLoaded', function() {
  var container = document.querySelector('[data-product-family-selector]');
  if (container) {
    new ProductFamilySelector(container);
  }
});

// Preload sibling products after the page has fully loaded and is idle
window.addEventListener('load', function() {
  var schedule = window.requestIdleCallback || function(cb) { setTimeout(cb, 1000); };
  schedule(function() {
    var container = document.querySelector('[data-product-family-selector]');
    if (container && container._pfsInstance) {
      container._pfsInstance.preloadFamilyProducts();
    }
  });
});

// Re-initialize when customizer reloads a section
document.addEventListener('shopify:section:load', function(event) {
  var container = event.target.querySelector('[data-product-family-selector]');
  if (container) {
    new ProductFamilySelector(container);
  }
});

// Handle browser back/forward after a product switch
window.addEventListener('popstate', function(event) {
  if (event.state && event.state.productHandle) {
    window.location.reload();
  }
});

// Re-position the active pill (and re-center the active option on
// mobile) when the viewport changes — e.g. customizer previewing
// mobile <-> desktop. The --slide-* CSS variables are stored in
// pixels by updateSliderPosition, so they don't reflow when the
// surrounding layout changes width. Without this, the pill keeps
// the dimensions and offset it had at the previous viewport.
var _pfsResizeTimer;
window.addEventListener('resize', function () {
  clearTimeout(_pfsResizeTimer);
  _pfsResizeTimer = setTimeout(function () {
    document.querySelectorAll('[data-product-family-selector]').forEach(function (container) {
      var inst = container._pfsInstance;
      if (!inst) return;
      inst.initSlider();
      inst.scrollActiveIntoView();
    });
  }, 150);
});
